import { Core } from "@faire/design-tokens";
import {
  SignUpType,
  SignUpElement,
} from "@faire/retailer-visitor-shared/lib/popSignUp";
import { trackSignUpClick } from "@faire/retailer-visitor-shared/lib/popSignUpUtils";
import { getSignUpLink } from "@faire/retailer-visitor-shared/lib/popSignUpUtils";
import {
  useSettingLoggedOutCategoryPageBannerImages,
  useAssignSettingLoggedOutCategoryPageBannerImages,
} from "@faire/retailer-visitor-shared/settings/useSettingLoggedOutCategoryPageBannerImages";
import { optimizeImageUrl } from "@faire/web--source/common/images/optimizeImages";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { Button } from "@faire/web--source/slate/Button";
import { Lock } from "@faire/web--source/slate/icons/foundation/Lock";
import { Column, Flex, Row } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { useResponsiveValue } from "@faire/web--source/ui/hooks/useResponsiveValue";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import { ILayoutElement } from "@faire/web-api--source/indigofair/data/ILayoutElement";
import React, { useEffect } from "react";
import { useHistory } from "react-router-dom";
import styled, { css } from "styled-components";

// TODO - move these to BE if this experiment wins
const categoryImages = {
  beauty_wellness: {
    mobile:
      "https://cdn.faire.com/fastly/0a7e75cf529c348c775e563d937566e4665c033c030273d488c65f833ddd03aa.jpeg",
    base: "https://cdn.faire.com/fastly/ca29b291f3cb3ddf3b59152c57d83a5239fbea8f8efa5a90edc5bc275bd3ddf4.jpeg",
    background_color: "#41252A",
  },
  food_drink: {
    mobile:
      "https://cdn.faire.com/fastly/0deb2b4b40754cb85ee93d10d6d377bc05a60052814b0ebfc40b31e9230f25c8.jpeg",
    base: "https://cdn.faire.com/fastly/4e02b206bd247a5516236314949243db4e31cb2fa42e6ea9b38b21b720f21123.jpeg",
    background_color: "#595604",
  },
  home_living: {
    mobile:
      "https://cdn.faire.com/fastly/03e9d940a0b1b605f4ea00a06b1ee2398578b1da8ca5d5cd986c296e05b9a757.jpeg",
    base: "https://cdn.faire.com/fastly/17d5ff28565e69e14abc585736fc71b367f177d6a5cd53b23ad196596cbe341a.jpeg",
    background_color: "#AB5528",
  },
  jewelry: {
    mobile:
      "https://cdn.faire.com/fastly/7195f347cbbf18bc62bd192fe4bef1524ab1606b5dabd6d4360297fd472403c1.jpeg",
    base: "https://cdn.faire.com/fastly/dfa8bf660df169f1f5024f0d33a51cfa5fc341946ef86d1ebdc8523373be7df3.jpeg",
    background_color: "#41252A",
  },
  kids_baby: {
    mobile:
      "https://cdn.faire.com/fastly/a9854a2599aa33a1f219f1cf31632d0959d8562b5cb0c0fcd3d0580244b0bf2b.jpeg",
    base: "https://cdn.faire.com/fastly/6e4ff6800f30a27c96136da3abadc81c9727a6ae7d1286a78c1256b5d967df79.jpeg",
    background_color: "#41252A",
  },
  men: {
    mobile:
      "https://cdn.faire.com/fastly/2c65c01360b14e3255fede0a25234e7171610f98ce0cc750a506eccbe1fbc051.jpeg",
    base: "https://cdn.faire.com/fastly/e4f391ea51bdea46706ef59754953b7883d2a1f95b4a8227dbaad795f354571d.jpeg",
    background_color: "#595604",
  },
  paper_novelty: {
    mobile:
      "https://cdn.faire.com/fastly/1a96571761db295a1df949ceee882b739cc47e6b70d7a7f8fbd43b0d11edbbe4.jpeg",
    base: "https://cdn.faire.com/fastly/da0cd5b704e96d1398a82652d25bf88bde47b24b3331ff11a2095d3f8320f588.jpeg",
    background_color: "#083114",
  },
  women: {
    mobile:
      "https://cdn.faire.com/fastly/057cc03a62a24eca1181138a4df74b1dd37582242a36ff4e05526629e3d10c7b.jpeg",
    base: "https://cdn.faire.com/fastly/d0edf1168efaec9c15a79d9f946a36ba06174f228dd4180624319e0dc588e767.jpeg",
    background_color: "#41252A",
  },
  pets: {
    mobile:
      "https://cdn.faire.com/fastly/710a7103530c9430bc46453234f3ab57ea675a35e599af77b603f0cf82ea110e.jpeg",
    base: "https://cdn.faire.com/fastly/e32dd8471862778cd7ada6a23dd6685c1057b300ab35b616736b2f6be37d5078.jpeg",
    background_color: "#083114",
  },
} as const;

type CategoryImageKey = keyof typeof categoryImages;

const getCategoryImage = (
  categoryId: string | undefined
): { mobile: string; base: string; background_color: string } | null => {
  if (!categoryId || !(categoryId in categoryImages)) {
    return null;
  }
  return categoryImages[categoryId as CategoryImageKey];
};

export const LoggedOutCategoryPageHeroBanner: React.FC<{
  layoutElement: ILayoutElement;
}> = ({ layoutElement }) => {
  const {
    background_image = IImage.build(),
    main_text,
    description_text,
    background_color,
    category_page_identifier,
  } = layoutElement;

  const isSSR = useIsSSR();
  const history = useHistory();
  const isUpdatedImage = useSettingLoggedOutCategoryPageBannerImages();

  const assignSetting = useAssignSettingLoggedOutCategoryPageBannerImages();

  useEffect(() => {
    assignSetting();
  }, [assignSetting]);

  const categoryImage = isUpdatedImage
    ? getCategoryImage(category_page_identifier)
    : null;

  const backgroundColor = categoryImage?.background_color ?? background_color;

  const imgSrc = useResponsiveValue({
    mobileAndAbove: optimizeImageUrl(background_image, isSSR, {
      width: 454,
      height: 214,
      resizeMethod: "fit",
    }),
    tabletAndAbove: optimizeImageUrl(background_image, isSSR, {
      width: 700,
      height: 325,
      resizeMethod: "fit",
    }),
    desktopAndAbove: optimizeImageUrl(background_image, isSSR, {
      width: 800,
      height: 372,
      resizeMethod: "fit",
    }),
    xxLargeDesktop: optimizeImageUrl(background_image, isSSR, {
      width: 1200,
      height: 558,
      resizeMethod: "fit",
    }),
  });

  const staticImageSrc =
    useResponsiveValue({
      mobileAndAbove: categoryImage?.mobile
        ? optimizeImageUrl({ url: categoryImage.mobile }, isSSR, {
            width: 454,
            height: 130,
            resizeMethod: "fit",
          })
        : undefined,
      tabletAndAbove: categoryImage?.base
        ? optimizeImageUrl({ url: categoryImage.base }, isSSR, {
            width: 1400,
            height: 327,
            resizeMethod: "fit",
          })
        : undefined,
    }) ?? imgSrc;

  const handleSignUpClick = () => {
    trackSignUpClick(
      SignUpType.GENERIC,
      SignUpElement.TOP_BANNER,
      "logged_out_category_page_hero_banner"
    );
    history.push(getSignUpLink(SignUpType.GENERIC, history.location));
  };

  return (
    <Wrapper $bannerColor={backgroundColor}>
      <BannerTextWrapper>
        <ContentWrapper>
          <Typography
            variant={{
              mobileAndAbove: "pageHeaderSerifRegular",
              tabletAndAbove: "displaySSerifRegular",
              xLargeAndAbove: "displayMSerifRegular",
            }}
            as="h1"
            color={Core.text.inverse}
          >
            {main_text}
          </Typography>
          <Spacer height={{ mobileAndAbove: "1/2x", tabletAndAbove: "2x" }} />
          <Typography
            variant={{
              mobileAndAbove: "paragraphSansRegular",
              xLargeAndAbove: "subheadingSansRegular",
            }}
            color={Core.text.inverse}
          >
            {description_text}
          </Typography>
          <Spacer height="3x" />
          <Button
            onClick={handleSignUpClick}
            variant="secondary"
            style={{ border: "none" }}
          >
            <Row align="center">
              <Lock lockType="foundation" fontSize={16} />
              <Spacer width={4} />
              <StrictLocalMsg
                defaultMessage="Unlock wholesale pricing"
                description={{
                  text: "sign up cta for discover page header",
                }}
              />
            </Row>
          </Button>
        </ContentWrapper>
      </BannerTextWrapper>
      <BannerImageWrapper $isUpdatedImage={isUpdatedImage}>
        <BannerImage
          src={isUpdatedImage ? staticImageSrc : imgSrc}
          data-test-id="banner-image"
          alt={background_image?.alt_text}
          $isUpdatedImage={isUpdatedImage}
        />
      </BannerImageWrapper>
    </Wrapper>
  );
};

const Wrapper = styled(Flex).attrs({
  direction: { mobileAndAbove: "column-reverse", tabletAndAbove: "row" },
})<{ $bannerColor?: string }>`
  width: 100%;
  position: relative;
  background-color: ${({ $bannerColor }) => $bannerColor};
  margin-bottom: ${getSpacing("3x")};
  ${responsiveMediaQueries.desktop} {
    margin-bottom: ${getSpacing("4x")};
  }
  ${responsiveMediaQueries.xLargeAndAbove} {
    margin-bottom: ${getSpacing("5x")};
  }
`;

const BannerTextWrapper = styled(Flex).attrs({
  direction: "column",
  justify: "space-between",
  align: "flex-start",
  gap: "3x",
})`
  flex-basis: 50%;
  z-index: 10;
  ${responsiveMediaQueries.xxlarge} {
    flex-basis: 672px;
    width: 672px;
  }
  ${responsiveMediaQueries.xLargeAndAbove} {
    padding: ${getSpacing(133, "6x")};
  }
  ${responsiveMediaQueries.desktop} {
    padding: ${getSpacing("10x", "6x")};
  }
  ${responsiveMediaQueries.tablet} {
    padding: ${getSpacing("8x", "4x")};
  }
  ${responsiveMediaQueries.mobile} {
    padding: ${getSpacing("3x", "2x")};
  }
`;

const BannerImageWrapper = styled(Flex)<{ $isUpdatedImage: boolean }>`
  flex-basis: 50%;
  ${responsiveMediaQueries.mobile} {
    max-height: 176px;
  }
  ${responsiveMediaQueries.xxlarge} {
    flex-basis: 100%;
  }

  ${({ $isUpdatedImage }) =>
    $isUpdatedImage &&
    css`
      ${responsiveMediaQueries.tabletAndAbove} {
        position: absolute;
        top: 0;
        right: 0;
        width: 100%;
        height: 100%;
      }
    `}
`;

const ContentWrapper = styled(Column).attrs({
  align: "flex-start",
})`
  word-break: break-word;
  width: 100%;
  ${responsiveMediaQueries.xxlarge} {
    width: 513px;
  }
`;

const BannerImage = styled.img<{ $isUpdatedImage: boolean }>`
  width: 100%;
  object-fit: cover;
  height: 0;
  min-height: 100%;
  ${({ $isUpdatedImage }) =>
    $isUpdatedImage
      ? css`
          object-position: right;
        `
      : css`
          object-position: center;
        `}
  ${responsiveMediaQueries.tabletAndBelow} {
    object-position: center;
  }
  ${responsiveMediaQueries.mobile} {
    height: 100%;
    max-height: 176px;
  }
`;
